/**
 * IRONMASTER PACKAGE BUILDER (IRON-9) — storefront controller
 * ===========================================================
 * Drives the guided builder rendered by sections/package-builder.liquid:
 *   · package selection reveals that package's ordered steps
 *   · select_one steps accept exactly one option; select_any accept many
 *   · steps unlock in sequence once earlier steps are satisfied
 *   · live "Everyday Package Discount" preview
 *
 * v3 (IRON-10): every option acts directly on the REAL Shopify cart — clicking
 * an unselected card calls /cart/add.js immediately (no in-memory "staging"
 * step, no separate "Add package to cart" button, no quantity control here —
 * the side cart already owns quantity/removal for anything already added).
 * Option UI (Added state, step unlock) is synced FROM the real cart on load
 * and after every cart:change/cart:refresh, so it stays correct even if the
 * cart is also edited elsewhere (the side cart, the cart drawer, etc).
 *
 * The builder only ADDS/REMOVES cart lines. The Shopify Function applies the
 * real discount at checkout; ironmaster-discount.js mirrors it for preview.
 */
(function () {
  "use strict";

  function init(root) {
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
    window.scrollTo(0, 0);

    var cards = root.querySelectorAll("[data-pkg-select]");
    var stepGroups = root.querySelectorAll("[data-pkg-steps]");
    var progressBlock = root.querySelector("[data-pkg-progress]");
    var previewEl = root.querySelector("[data-im-discount-preview]");
    var optionsBar = root.querySelector("[data-pkg-options-bar]");
    var optionsCountEl = root.querySelector("[data-pkg-options-count]");
    var activePkg = null;
    var viewCompact = true;
    var cartByVariant = {}; // variantId (string) -> { key, quantity }
    var cartSynced = false;
    var pendingCartSync = null;
    var busy = false;
    var CART_LINE_LIMIT = 17;

    function cartLineCount() {
      return Object.keys(cartByVariant).length;
    }

    // ── Package card carousel (mobile only — desktop is a static grid) ───
    (function initCarousel() {
      var track = root.querySelector("[data-pkg-carousel]");
      var prevBtn = root.querySelector("[data-pkg-carousel-prev]");
      var nextBtn = root.querySelector("[data-pkg-carousel-next]");
      if (!track || !prevBtn || !nextBtn) return;

      function step() {
        var card = track.querySelector(".pkg-card");
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 16);
        return card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.46;
      }

      function updateArrows() {
        var max = track.scrollWidth - track.clientWidth - 1;
        prevBtn.disabled = track.scrollLeft <= 0;
        nextBtn.disabled = track.scrollLeft >= max;
      }

      prevBtn.addEventListener("click", function () { track.scrollBy({ left: -step(), behavior: "smooth" }); });
      nextBtn.addEventListener("click", function () { track.scrollBy({ left: step(), behavior: "smooth" }); });
      track.addEventListener("scroll", updateArrows);
      window.addEventListener("resize", updateArrows);
      updateArrows();
    })();

    // ── Package selection (just switches which step group is shown) ──────
    var ACTIVE_PKG_ATTRIBUTE = "ironmaster_active_package";
    // Persisted on the CART itself (not sessionStorage/localStorage) so it
    // travels with the shopper's real cart — across tabs, browsers, and
    // devices once they're logged in — instead of being stranded on
    // whichever browser happened to make the selection.
    function persistActivePackage(id) {
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ attributes: { ironmaster_active_package: id } })
      }).catch(function (e) { /* best-effort — never break package selection over this */ });
    }
    var FOUNDATION_ATTRIBUTE = "ironmaster_foundation_variants";
    function persistFoundationChoices() {
      var group = activeGroup();
      if (!group) return;
      var vals = [];
      group.querySelectorAll('.pkg-step[data-rule="select_one"]').forEach(function (step) {
        var v = step.getAttribute("data-foundation-variant");
        if (v) vals.push(v);
      });
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ attributes: { ironmaster_foundation_variants: vals.join(",") } })
      }).catch(function (e) {});
    }
    function forgetPersistedSelection() {
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ attributes: { ironmaster_active_package: "", ironmaster_foundation_variants: "" } })
      }).catch(function (e) {});
    }
    function restoreFoundationChoices(cart) {
      var raw = cart && cart.attributes && cart.attributes[FOUNDATION_ATTRIBUTE];
      if (!raw) return;
      var chosen = raw.split(",").map(function (s) { return s.trim(); }).filter(Boolean);
      if (!chosen.length) return;
      var group = activeGroup();
      if (!group) return;
      group.querySelectorAll('.pkg-step[data-rule="select_one"]').forEach(function (step) {
        if (step.getAttribute("data-foundation-variant")) return;
        var opts = step.querySelectorAll(".pkg-option");
        for (var i = 0; i < opts.length; i++) {
          var match = variantIdsFor(opts[i]).filter(function (id) { return chosen.indexOf(id) !== -1; })[0];
          if (match) {
            step.setAttribute("data-foundation-variant", match);
            break;
          }
        }
      });
    }
    function activatePackage(id, opts) {
      var scroll = !opts || opts.scroll !== false;
      var card = root.querySelector('[data-pkg-select="' + id + '"]');
      activePkg = id;
      // Remember which package the shopper actually chose, so a reload can
      // restore THAT package specifically — see autoSelectFromCart below,
      // which otherwise can't tell "this product is package X's own anchor"
      // apart from "this product is just an attachment someone picked while
      // building a different package" when the same product is both.
      persistActivePackage(id);
      cards.forEach(function (c) { c.setAttribute("aria-pressed", String(c === card)); });
      stepGroups.forEach(function (g) {
        var on = g.getAttribute("data-pkg-steps") === id;
        g.classList.toggle("is-active", on);
      });
      // Carry over whatever Filter & Sort setting (and grid density) is
      // currently active to this package's own attachment grid — both are
      // global settings, not per-package, now that the Filter&Sort bar
      // itself is a single shared instance (see initFilterDrawer below and
      // the grid density toggle further down).
      var group = root.querySelector('[data-pkg-steps="' + id + '"]');
      if (root.__pkgApplyFilterState) {
        var newGrid = group && group.querySelector("[data-pkg-options-paginated]");
        if (newGrid) root.__pkgApplyFilterState(newGrid);
      }
      if (group) {
        group.querySelectorAll(".pkg-options").forEach(function (grid) {
          grid.classList.toggle("pkg-options--compact", viewCompact);
        });
      }
      if (progressBlock) progressBlock.hidden = false;
      if (optionsBar) {
        optionsBar.hidden = false;
        if (optionsCountEl) optionsCountEl.textContent = group ? group.getAttribute("data-options-count") : "";
      }
      syncUI();
      if (scroll && card) card.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    cards.forEach(function (card) {
      card.addEventListener("click", function () {
        activatePackage(card.getAttribute("data-pkg-select"));
      });
    });

    // ── Auto-select a package already in progress on page load/reload ──────
    // If the cart already has an anchor product for one of the packages
    // (added via a previous visit, the side cart, or another page), show
    // that package's steps instead of leaving nothing selected.
    var hasAutoSelected = false;
    function groupHasAnchorInCart(group) {
      var found = false;
      group.querySelectorAll(".pkg-option[data-anchor='true']").forEach(function (opt) {
        if (!found && cartQtyForOption(opt) > 0) found = true;
      });
      return found;
    }
    function autoSelectFromCart(cart) {
      if (hasAutoSelected || activePkg) return;
      hasAutoSelected = true;

      // Prefer the package the shopper actually selected last (persisted on
      // the cart itself by activatePackage above) over guessing from cart
      // contents — the same product can be one package's own anchor AND
      // another package's attachment option, so scanning every anchor in the
      // cart is ambiguous whenever that overlap happens (e.g. a QLDB dumbbell
      // set is its own package's anchor, but is also offered as an IM3000
      // attachment). Reading it off the cart (not sessionStorage) means this
      // still works after switching browsers/devices on the same account.
      // Still verify it's still a real anchor match before trusting it, in
      // case the cart changed enough since (e.g. that line was removed).
      var remembered = cart && cart.attributes && cart.attributes[ACTIVE_PKG_ATTRIBUTE];
      if (remembered) {
        var rememberedGroup = root.querySelector('[data-pkg-steps="' + remembered + '"]');
        if (rememberedGroup && groupHasAnchorInCart(rememberedGroup)) {
          activatePackage(remembered, { scroll: false });
          return;
        }
      }

      var match = null;
      root.querySelectorAll(".pkg-option[data-anchor='true']").forEach(function (opt) {
        if (match) return;
        if (cartQtyForOption(opt) > 0) {
          var group = opt.closest("[data-pkg-steps]");
          if (group) match = group.getAttribute("data-pkg-steps");
        }
      });
      if (match) activatePackage(match, { scroll: false });
    }

    // ── Grid density toggle (compact = 4/row, dense = 6/row) — the toggle
    // itself is a single shared control (see the Filter&Sort bar hoist
    // above), so the preference is global; viewCompact carries it over to
    // whichever package activatePackage() switches to next ──────────────
    root.addEventListener("click", function (e) {
      var viewBtn = e.target.closest(".pkg-options__view-icon");
      if (!viewBtn) return;
      var toggle = viewBtn.closest(".pkg-options__view-toggle");
      var group = activeGroup();
      if (!toggle || !group) return;
      toggle.querySelectorAll(".pkg-options__view-icon").forEach(function (btn) {
        var active = btn === viewBtn;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-pressed", String(active));
      });
      viewCompact = viewBtn.getAttribute("data-view") === "compact";
      group.querySelectorAll(".pkg-options").forEach(function (grid) {
        grid.classList.toggle("pkg-options--compact", viewCompact);
      });
    });

    function activeGroup() {
      return root.querySelector('[data-pkg-steps="' + activePkg + '"]');
    }

    function activeDiscountRuleIds() {
      var group = activeGroup();
      var raw = group && group.getAttribute("data-discount-rule-ids");
      if (!raw) return null;
      var ids = raw.split(",").map(function (id) { return id.trim(); }).filter(Boolean);
      return ids.length ? ids : null;
    }

    // ── Cart state ────────────────────────────────────────────────────────
    var refreshSeq = 0;
    function refresh() {
      var mySeq = ++refreshSeq;
      pendingCartSync = fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { if (mySeq === refreshSeq) applyCart(cart); })
        .then(function () { cartSynced = true; })
        .catch(function (err) { console && console.warn && console.warn("[PackageBuilder]", err); })
        .then(function () { pendingCartSync = null; });
      return pendingCartSync;
    }

    function cartQtyFor(variantId) {
      var line = cartByVariant[String(variantId)];
      return line ? line.quantity : 0;
    }

    function variantIdsFor(opt) {
      var raw = opt.getAttribute("data-variant-ids") || opt.getAttribute("data-variant-id") || "";
      return raw.split(",").map(function (s) { return s.trim(); }).filter(Boolean);
    }

    function cartQtyForOption(opt) {
      return variantIdsFor(opt).reduce(function (sum, id) { return sum + cartQtyFor(id); }, 0);
    }

    function removeOptionFromCart(opt) {
      return variantIdsFor(opt)
        .filter(function (id) { return cartQtyFor(id) > 0; })
        .reduce(function (chain, id) {
          return chain.then(function () { return setQty(id, 0); });
        }, Promise.resolve());
    }

    // ── Sync every option's UI (Added state, step unlock) from the cart ─────
    function syncUI() {
      root.querySelectorAll(".pkg-option").forEach(function (opt) {
        if (opt.classList.contains("pkg-option--placeholder")) return;
        var qty = cartQtyForOption(opt);
        opt.setAttribute("aria-pressed", qty > 0 ? "true" : "false");
      });
      root.querySelectorAll('.pkg-step[data-rule="select_one"]').forEach(function (step) {
        var pressed = Array.prototype.filter.call(
          step.querySelectorAll(".pkg-option"),
          function (o) { return o.getAttribute("aria-pressed") === "true"; }
        );
        if (pressed.length <= 1) return;
        var chosenVar = step.getAttribute("data-foundation-variant");
        var keep = null;
        pressed.forEach(function (o) { if (!keep && variantIdsFor(o).indexOf(String(chosenVar)) !== -1) keep = o; });
        if (!keep) keep = pressed[0];
        pressed.forEach(function (o) { if (o !== keep) o.setAttribute("aria-pressed", "false"); });
      });
      updateProgression();
    }

    // ── Step progression (reads aria-pressed, which syncUI() keeps mirrored
    //    to the real cart — so this stays accurate without further changes) ─
    function stepSatisfied(step) {
      var rule = step.getAttribute("data-rule");
      var min = parseInt(step.getAttribute("data-min-items"), 10) || 0;
      var chosen = step.querySelectorAll(".pkg-option[aria-pressed='true']:not([data-anchor-duplicate='true'])").length;
      if (rule === "select_one") return chosen >= 1;
      return min > 0 ? chosen >= min : true;
    }

    function hasAnySelection(step) {
      return step.querySelector(".pkg-option[aria-pressed='true']:not([data-anchor-duplicate='true'])") !== null;
    }

    var LOCKED_NOTE_TEXT =
      "Locked \u2014 complete the step above again to edit these. Anything you already added is still in your cart.";

    function setLockedNote(step, show) {
      var col = step.querySelector(".pkg-step__heading-col");
      if (!col) return;
      var note = col.querySelector(".pkg-step__locked-note");
      if (show) {
        if (!note) {
          note = document.createElement("p");
          note.className = "pkg-step__locked-note";
          note.textContent = LOCKED_NOTE_TEXT;
          col.appendChild(note);
        }
      } else if (note) {
        note.parentNode.removeChild(note);
      }
    }

    function resetStep(step) {
      var first = step.getAttribute("data-step-index") === "0";
      step.classList.toggle("is-unlocked", first);
      step.setAttribute("aria-disabled", first ? "false" : "true");
      step.classList.remove("is-complete");
      setLockedNote(step, false);
    }

    function lockAllSteps() {
      root.querySelectorAll(".pkg-step").forEach(resetStep);
    }

    function resetBuilder() {
      cards.forEach(function (c) { c.setAttribute("aria-pressed", "false"); });
      stepGroups.forEach(function (g) { g.classList.remove("is-active"); });
      root.querySelectorAll(".pkg-step[data-foundation-variant]").forEach(function (step) {
        step.removeAttribute("data-foundation-variant");
      });
      if (progressBlock) progressBlock.hidden = true;
      if (optionsBar) optionsBar.hidden = true;
      lockAllSteps();
      window.scrollTo(0, 0);
    }

    function updateProgression() {
      var group = activeGroup();
      if (!group) { lockAllSteps(); return; }
      var steps = Array.prototype.slice.call(group.querySelectorAll(".pkg-step"));
      var unlockNext = true;
      steps.forEach(function (step) {
        var hasSelection = hasAnySelection(step);
        var unlocked = unlockNext;
        step.classList.toggle("is-unlocked", unlocked);
        step.setAttribute("aria-disabled", unlocked ? "false" : "true");
        var satisfied = stepSatisfied(step);
        step.classList.toggle("is-complete", unlocked && satisfied && hasSelection);
        setLockedNote(step, !unlocked && hasSelection);
        unlockNext = unlockNext && satisfied;
      });
    }

    // ── Cart mutation ────────────────────────────────────────────────────
    // Updates this option's Added state from the request's OWN response,
    // instead of waiting for the separate /cart.js fetch that cart:refresh
    // triggers afterward (for the side cart etc) — that second round trip is
    // exactly what made the card feel slow to flip after clicking it.
    function setQty(variantId, newQty) {
      newQty = Math.max(0, newQty);
      var existing = cartByVariant[String(variantId)];
      if (existing) {
        // /cart/change.js conveniently returns the FULL updated cart, so we
        // can resync everything from it directly — no extra fetch needed.
        return fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ id: existing.key, quantity: newQty })
        })
          .then(function (r) { if (!r.ok) throw new Error("cart request failed"); return r.json(); })
          .then(function (cart) { applyCart(cart); return cart; });
      }
      if (newQty <= 0) return Promise.resolve();
      // /cart/add.js only returns the added line(s), not the whole cart, so
      // just fold this one variant into our local state — good enough to
      // flip this card's Added state immediately; totals catch up shortly
      // after via the cart:refresh-triggered full fetch.
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: newQty }] })
      })
        .then(function (r) { if (!r.ok) throw new Error("cart request failed"); return r.json(); })
        .then(function (data) {
          var added = (data.items && data.items[0]) || data;
          cartByVariant[String(variantId)] = { key: added.key, quantity: added.quantity || newQty };
          syncUI();
          return data;
        });
    }

    function applyCart(cart) {
      cartByVariant = {};
      (cart.items || []).forEach(function (item) {
        cartByVariant[String(item.variant_id)] = { key: item.key, quantity: item.quantity };
      });
      if (!busy && activePkg) {
        var currentGroup = root.querySelector('[data-pkg-steps="' + activePkg + '"]');
        if (currentGroup && !groupHasAnchorInCart(currentGroup)) {
          activePkg = null;
          hasAutoSelected = false;
          resetBuilder();
          forgetPersistedSelection();
        }
      }
      autoSelectFromCart(cart);
      restoreFoundationChoices(cart);
      syncUI();
      renderPreview(cart);
    }

    // For select_one steps, only one anchor may be in the cart at a time —
    // adding a new one first removes whichever sibling anchor was already there.
    function siblingAnchorsToClear(step, exceptOpt) {
      var els = [];
      step.querySelectorAll(".pkg-option[data-anchor='true']").forEach(function (opt) {
        if (opt !== exceptOpt && cartQtyForOption(opt) > 0) els.push(opt);
      });
      return els;
    }

    function clearAnchors(els) {
      return els.reduce(function (chain, opt) {
        return chain.then(function () { return removeOptionFromCart(opt); });
      }, Promise.resolve());
    }

    function setLoading(els, loading) {
      els.forEach(function (el) {
        el.classList.toggle("is-loading", loading);
        el.setAttribute("aria-disabled", loading ? "true" : "false");
      });
    }

    // loadingEls: the option card(s) this request is acting on, so each card
    // shows its own spinner rather than one generic page-wide loading state.
    function mutate(fn, loadingEls) {
      if (busy) return;
      busy = true;
      if (loadingEls) setLoading(loadingEls, true);
      fn()
        .then(function () {
          document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        })
        .catch(function (err) { console && console.warn && console.warn("[PackageBuilder]", err); })
        .then(function () {
          busy = false;
          if (loadingEls) setLoading(loadingEls, false);
        });
    }

    // ── Option interaction (delegated) ───────────────────────────────────
    // Before selection there's no visible button at all (per Figma) — the
    // whole card is the click target. After selection, the only actions are
    // the trash icon (remove) and a static "Added" label; quantity isn't
    // adjustable here at all anymore since the side cart already owns that.
    root.addEventListener("click", function (e) {
      var opt = e.target.closest(".pkg-option");
      if (!opt || opt.classList.contains("pkg-option--placeholder") || opt.getAttribute("aria-disabled") === "true") return;

      var step = opt.closest(".pkg-step");
      if (!step || !step.classList.contains("is-unlocked")) return;

      var variantId = opt.getAttribute("data-variant-id");
      var isAnchor = opt.getAttribute("data-anchor") === "true";
      var current = cartQtyForOption(opt);

      if (e.target.closest("[data-option-remove]")) {
        e.stopPropagation();
        mutate(function () { return removeOptionFromCart(opt); }, [opt]);
        return;
      }

      if (opt.getAttribute("data-unavailable") === "true") return;

      if (current > 0) return; // already added — only the trash icon does anything now
      if (!e.target.closest(".pkg-option__select") && !e.target.closest(".pkg-option__add")) return;

      if (opt.getAttribute("data-needs-choice") === "true") {
        e.stopPropagation();
        var chooser = document.getElementById(opt.getAttribute("data-choose-target"));
        var openChooser = function () {
          if (cartLineCount() >= CART_LINE_LIMIT) {
            if (window.IronmasterCartLimit) window.IronmasterCartLimit.open();
            return;
          }
          if (chooser) chooser.setAttribute("open", "");
        };
        if (cartSynced) openChooser();
        else (pendingCartSync || refresh()).then(openChooser);
        return;
      }

      function performAdd() {
        if (cartLineCount() >= CART_LINE_LIMIT) {
          if (window.IronmasterCartLimit) window.IronmasterCartLimit.open();
          return;
        }
        if (isAnchor && step.getAttribute("data-rule") === "select_one") {
          // Re-persist here too, on top of the card-click/auto-select triggers
          // above — this is the exact moment the anchor actually lands in the
          // real cart, so it's the most important point for the cart's own
          // "active package" record to be correct, and a cheap safety net in
          // case an earlier persist call (best-effort, no retry) got dropped.
          persistActivePackage(activePkg);
          step.setAttribute("data-foundation-variant", variantId);
          persistFoundationChoices();
          var siblings = siblingAnchorsToClear(step, opt);
          mutate(function () {
            return clearAnchors(siblings).then(function () { return setQty(variantId, 1); });
          }, siblings.concat([opt]));
        } else {
          mutate(function () { return setQty(variantId, 1); }, [opt]);
        }
      }

      if (cartSynced) {
        performAdd();
      } else {
        // The initial /cart.js fetch (see refresh()) hasn't resolved yet — a
        // shopper can click within that window right after a page load —
        // hold this add until the real line count is known, instead of
        // reading the still-empty cartByVariant as "0 lines, always allowed".
        (pendingCartSync || refresh()).then(performAdd);
      }
    });

    function addChosenVariant(opt, variantId, quantity, chooser) {
      var step = opt.closest(".pkg-step");
      var isAnchor = opt.getAttribute("data-anchor") === "true";
      var run = function () {
        if (chooser && chooser.hide) chooser.hide();
        if (cartLineCount() >= CART_LINE_LIMIT && cartQtyFor(variantId) === 0) {
          if (window.IronmasterCartLimit) window.IronmasterCartLimit.open();
          return;
        }
        if (isAnchor && step && step.getAttribute("data-rule") === "select_one") {
          persistActivePackage(activePkg);
          step.setAttribute("data-foundation-variant", String(variantId));
          persistFoundationChoices();
          var siblings = siblingAnchorsToClear(step, opt);
          mutate(function () {
            return clearAnchors(siblings).then(function () { return setQty(variantId, quantity); });
          }, siblings.concat([opt]));
        } else {
          mutate(function () { return setQty(variantId, quantity); }, [opt]);
        }
      };
      if (cartSynced) run();
      else (pendingCartSync || refresh()).then(run);
    }

    document.addEventListener("submit", function (e) {
      var form = e.target;
      if (!form || typeof form.matches !== "function" || !form.matches('form[action*="/cart/add"]')) return;
      var chooser = form.closest("quick-buy-modal[data-pkg-chooser]");
      if (!chooser) return;
      var opt = root.querySelector('.pkg-option[data-choose-target="' + chooser.id + '"]');
      if (!opt) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var data = new FormData(form);
      var variantId = data.get("id");
      if (!variantId) return;
      var quantity = parseInt(data.get("quantity"), 10) || 1;
      addChosenVariant(opt, variantId, quantity, chooser);
    }, true);

    // ── Discount preview — always reflects the REAL cart, not a staged selection ─
    function renderPreview(cart) {
      if (!previewEl || !window.IronmasterDiscount) return;
      var IM = window.IronmasterDiscount;
      var handles = (cart.items || []).map(function (item) { return item.handle; });
      IM.fetchProductTagsForHandles(handles).then(function (tagsByHandle) {
        var engineLines = (cart.items || []).map(function (item) {
          var tags = tagsByHandle[item.handle] || [];
          if (!tags.length) tags = IM.tagsForSku(item.sku);
          return {
            id: item.key,
            quantity: item.quantity,
            merchandise: {
              sku: item.sku || "",
              price: { amount: (item.original_price / 100).toFixed(2) },
              product: {
                id: String(item.product_id),
                title: item.product_title,
                tags: tags
              }
            }
          };
        });
        previewEl.innerHTML = IM.renderHTML(engineLines, cart.total_price / 100, null) || '<p class="im-discount__best-note">Add qualifying items to unlock your Everyday Package Discount</p>';
      });
    }

    document.addEventListener("cart:change", refresh);
    document.addEventListener("cart:refresh", refresh);
    refresh();

    // ── Attachment grid pagination (IRON-9) ──────────────────────────────
    // Client-side only: every matching product is already in the DOM (same
    // as before), this just shows/hides which page's cards are visible.
    // Exposes a small controller on the grid element itself (grid.__pkgGrid)
    // so the Filter & Sort drawer below can re-sort/filter this same set
    // and ask pagination to recompute instead of duplicating this logic.
    root.querySelectorAll("[data-pkg-options-paginated]").forEach(function (grid) {
      var pageSize = parseInt(grid.getAttribute("data-page-size"), 10) || 20;
      var allItems = Array.prototype.slice.call(grid.querySelectorAll(".pkg-option"));
      var visibleItems = allItems;
      var pagination = grid.nextElementSibling;
      if (!pagination || !pagination.hasAttribute("data-pkg-pagination")) return;
      var page = 1;
      var statusEl = pagination.querySelector("[data-page-status]");
      var prevBtn = pagination.querySelector("[data-page-prev]");
      var nextBtn = pagination.querySelector("[data-page-next]");

      function render() {
        var totalPages = Math.max(1, Math.ceil(visibleItems.length / pageSize));
        if (page > totalPages) page = totalPages;
        allItems.forEach(function (item) { item.hidden = true; });
        visibleItems.forEach(function (item, i) {
          var onPage = Math.floor(i / pageSize) + 1;
          item.hidden = onPage !== page;
        });
        pagination.hidden = totalPages <= 1;
        statusEl.innerHTML = "<b>Page " + page + "</b> of " + totalPages;
        prevBtn.disabled = page <= 1;
        nextBtn.disabled = page >= totalPages;
      }

      function scrollGridToTop() {
        var step = grid.closest(".pkg-step");
        var target = (step && step.querySelector(".pkg-step__head")) || grid;
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      prevBtn.addEventListener("click", function () { if (page > 1) { page -= 1; render(); scrollGridToTop(); } });
      nextBtn.addEventListener("click", function () { var totalPages = Math.max(1, Math.ceil(visibleItems.length / pageSize)); if (page < totalPages) { page += 1; render(); scrollGridToTop(); } });

      render();

      grid.__pkgGrid = {
        // Called by the filter drawer with a sorted + filtered subset (or
        // null to reset back to the original unsorted/unfiltered set).
        applyView: function (nextVisible) {
          visibleItems = nextVisible || allItems;
          page = 1;
          render();
        },
        allItems: allItems
      };
    });

    // ── Filter & Sort drawer (Step 2 attachments) ────────────────────────
    // The chosen sort/filter is treated as one global setting for the whole
    // builder (not per-package) — switching to a different package's
    // attachment grid re-applies whatever was last set here automatically.
    (function initFilterDrawer() {
      var overlay = root.querySelector("[data-pkg-filter-overlay]");
      var drawer = root.querySelector("[data-pkg-filter-drawer]");
      if (!overlay || !drawer) return;
      var closeBtn = drawer.querySelector("[data-pkg-filter-close]");
      var applyBtn = drawer.querySelector("[data-pkg-filter-apply]");
      var clearBtn = drawer.querySelector("[data-pkg-filter-clear]");
      var minInput = drawer.querySelector("[data-pkg-filter-price-min]");
      var maxInput = drawer.querySelector("[data-pkg-filter-price-max]");
      var saleInput = drawer.querySelector("[data-pkg-filter-sale]");
      var featuredInput = drawer.querySelector("[data-pkg-filter-featured]");
      var categoriesBody = drawer.querySelector("[data-pkg-filter-categories]");
      var systemsBody = drawer.querySelector("[data-pkg-filter-systems]");
      var targetGrid = null;
      var filterState = { sort: "relevancy", min: NaN, max: NaN, sale: false, featured: false, categories: [], systems: [] };

      var SYSTEM_LABELS = (function () {
        var el = root.querySelector("[data-pkg-system-labels]");
        if (!el) return {};
        try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
      })();

      function itemSystems(item) {
        var tags = (item.getAttribute("data-tags") || "").split(",");
        var out = [];
        tags.forEach(function (tag) {
          var t = tag.trim();
          if (t.indexOf("collection:") !== 0) return;
          var handle = t.slice("collection:".length);
          if (SYSTEM_LABELS[handle]) out.push(handle);
        });
        return out;
      }

      function itemIsFeatured(item) {
        return (item.getAttribute("data-tags") || "").split(",").some(function (tag) {
          return tag.trim() === "collection:featured";
        });
      }

      // Categories/Systems checkboxes depend on which products are actually
      // in THIS package's collection, so rebuild them each time the drawer
      // opens for a (possibly different) grid — re-checking whichever
      // values are still active in filterState.
      function populateFacets(grid) {
        var items = (grid.__pkgGrid && grid.__pkgGrid.allItems) || [];

        var categories = [];
        items.forEach(function (item) {
          var cat = item.getAttribute("data-category");
          if (cat && categories.indexOf(cat) === -1) categories.push(cat);
        });
        categories.sort();
        categoriesBody.innerHTML = categories.length
          ? categories.map(function (cat) {
              var checked = filterState.categories.indexOf(cat) !== -1 ? " checked" : "";
              return '<label class="pkg-filter-radio"><input type="checkbox" value="' + cat.replace(/"/g, "&quot;") + '" data-pkg-filter-category' + checked + '><span>' + cat + "</span></label>";
            }).join("")
          : '<p class="pkg-filter-group__soon-text">No categories found</p>';

        var systems = [];
        items.forEach(function (item) {
          itemSystems(item).forEach(function (handle) { if (systems.indexOf(handle) === -1) systems.push(handle); });
        });
        systems.sort(function (a, b) { return SYSTEM_LABELS[a].localeCompare(SYSTEM_LABELS[b]); });
        systemsBody.innerHTML = systems.length
          ? systems.map(function (handle) {
              var checked = filterState.systems.indexOf(handle) !== -1 ? " checked" : "";
              return '<label class="pkg-filter-radio"><input type="checkbox" value="' + handle + '" data-pkg-filter-system' + checked + '><span>' + SYSTEM_LABELS[handle] + "</span></label>";
            }).join("")
          : '<p class="pkg-filter-group__soon-text">No other systems found</p>';
      }

      function open(grid) {
        targetGrid = grid;
        populateFacets(grid);
        overlay.hidden = false;
        drawer.hidden = false;
        // Force layout before adding the "open" class so the slide-in transition runs.
        overlay.offsetHeight;
        overlay.classList.add("is-open");
        drawer.classList.add("is-open");
        drawer.setAttribute("aria-hidden", "false");
      }

      function close() {
        overlay.classList.remove("is-open");
        drawer.classList.remove("is-open");
        drawer.setAttribute("aria-hidden", "true");
        window.setTimeout(function () { overlay.hidden = true; drawer.hidden = true; }, 260);
      }

      root.querySelectorAll("[data-pkg-filter-trigger]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var stepsGroup = activeGroup();
          var grid = stepsGroup && stepsGroup.querySelector("[data-pkg-options-paginated]");
          if (grid) open(grid);
        });
      });
      closeBtn.addEventListener("click", close);
      overlay.addEventListener("click", close);
      document.addEventListener("keydown", function (e) { if (e.key === "Escape" && !drawer.hidden) close(); });
      saleInput.addEventListener("click", function () {
        saleInput.setAttribute("aria-pressed", saleInput.getAttribute("aria-pressed") === "true" ? "false" : "true");
      });

      // Re-sorts/filters whichever grid is passed in using the CURRENT
      // filterState — used both by "Apply" and whenever a different
      // package's grid becomes active, so the setting carries over.
      function applyStateToGrid(grid) {
        if (!grid || !grid.__pkgGrid) return;
        var isDefault = filterState.sort === "relevancy" && isNaN(filterState.min) && isNaN(filterState.max)
          && !filterState.sale && !filterState.featured && !filterState.categories.length && !filterState.systems.length;
        if (isDefault) { grid.__pkgGrid.applyView(null); return; }

        var items = grid.__pkgGrid.allItems.filter(function (item) {
          var price = parseFloat(item.getAttribute("data-price")) || 0;
          if (!isNaN(filterState.min) && price < filterState.min * 100) return false;
          if (!isNaN(filterState.max) && price > filterState.max * 100) return false;
          if (filterState.sale) {
            var comparePrice = parseFloat(item.getAttribute("data-compare-price")) || 0;
            if (!(comparePrice > price)) return false;
          }
          if (filterState.featured && !itemIsFeatured(item)) return false;
          if (filterState.categories.length && filterState.categories.indexOf(item.getAttribute("data-category")) === -1) return false;
          if (filterState.systems.length) {
            var has = itemSystems(item).some(function (s) { return filterState.systems.indexOf(s) !== -1; });
            if (!has) return false;
          }
          return true;
        });

        if (filterState.sort === "price-asc") {
          items.sort(function (a, b) { return (parseFloat(a.getAttribute("data-price")) || 0) - (parseFloat(b.getAttribute("data-price")) || 0); });
        } else if (filterState.sort === "price-desc") {
          items.sort(function (a, b) { return (parseFloat(b.getAttribute("data-price")) || 0) - (parseFloat(a.getAttribute("data-price")) || 0); });
        } else if (filterState.sort === "newest") {
          items.sort(function (a, b) { return (parseFloat(b.getAttribute("data-created")) || 0) - (parseFloat(a.getAttribute("data-created")) || 0); });
        } // "relevancy" — leave in original (server-rendered) order

        // Physically reorder the matching cards to the sorted order (pagination
        // reads DOM order); filtered-out cards are left in place but hidden.
        items.forEach(function (item) { grid.appendChild(item); });
        grid.__pkgGrid.applyView(items);
      }

      function apply() {
        filterState.sort = (drawer.querySelector('input[name="pkg-filter-sort"]:checked') || {}).value || "relevancy";
        filterState.min = parseFloat(minInput.value);
        filterState.max = parseFloat(maxInput.value);
        filterState.sale = saleInput.getAttribute("aria-pressed") === "true";
        filterState.featured = featuredInput.checked;
        filterState.categories = Array.prototype.slice.call(categoriesBody.querySelectorAll("[data-pkg-filter-category]:checked")).map(function (el) { return el.value; });
        filterState.systems = Array.prototype.slice.call(systemsBody.querySelectorAll("[data-pkg-filter-system]:checked")).map(function (el) { return el.value; });
        applyStateToGrid(targetGrid);
        close();
      }

      function clearAll() {
        drawer.querySelector('input[name="pkg-filter-sort"][value="relevancy"]').checked = true;
        minInput.value = "";
        maxInput.value = "";
        saleInput.setAttribute("aria-pressed", "false");
        featuredInput.checked = false;
        categoriesBody.querySelectorAll("[data-pkg-filter-category]").forEach(function (el) { el.checked = false; });
        systemsBody.querySelectorAll("[data-pkg-filter-system]").forEach(function (el) { el.checked = false; });
        filterState = { sort: "relevancy", min: NaN, max: NaN, sale: false, featured: false, categories: [], systems: [] };
        applyStateToGrid(targetGrid);
      }

      // Exposed so package-switching (elsewhere in this file) can re-apply
      // the current global filter/sort to whichever grid just became active.
      root.__pkgApplyFilterState = applyStateToGrid;

      applyBtn.addEventListener("click", apply);
      clearBtn.addEventListener("click", clearAll);
    })();
  }

  function boot() {
    document.querySelectorAll("package-builder").forEach(init);
  }
  if (document.readyState !== "loading") boot();
  else document.addEventListener("DOMContentLoaded", boot);
})();
